import { html } from 'lit-element';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors as userSelectors } from '@elab/core/store/user';
import { actions as orderActions, selectors as orderSelectors } from '@elab/core/store/orders';
import {
    actions as personalActions,
    selectors as personalSelectors,
} from '@elab/components/connected/forms/PersonalInformationForm/redux';
import { actions as FormAction } from '@elab/components/connected/forms/Form/redux';
import { personalInformation } from '@elab/core/utils/constants';
import getSelectors from '@elab/core/utils/dynaForms/selectors';
import { cacheSelector } from '@elab/core/utils/webComponent';

class ContactUsOrderSelection extends LightLitElement {
    static get properties() {
        return {
            title: { type: String, attribute: 'order-title' },
            hideOptional: { type: Boolean, attribute: 'hide-optional' },
            placeholder: { type: String, attribute: 'place-holder' },
            storeName: { type: String, attribute: 'store-name' },
            fieldName: { type: String, attribute: 'field-name' },
            fieldLabel: { type: String, attribute: 'field-label' },
            pageSize: { type: Number, attribute: 'page-size' },
            error: { type: Boolean, reflect: true },
            errorLabel: { type: String, attribute: 'error-label' },
            tooltip: { type: Boolean, attribute: 'tool-tip' },
            orders: { type: Array, attribute: false },
            isUserLoggedIn: { type: String, attribute: false },
            subject: { type: String, attribute: false },
            readOnly: { type: Boolean, attribute: 'read-only' },
            defaultValue: { type: String, attribute: 'default-value' },
            selectedValue: { type: String, attribute: false },
            noEmptyLine: { type: Boolean, attribute: 'no-empty-line' },
            syncSelectValue: { type: Boolean, attribute: 'sync-select-value' },
            localError: { type: Boolean, attribute: false },
            disabledNativePlaceholderOption: {
                type: Boolean,
                attribute: 'disabled-native-placeholder-option',
            },
            scrollModifier: {
                type: String,
                attribute: 'scroll-modifier',
            },
            useLabelAsPlaceholder: {
                type: Boolean,
                attribute: 'use-label-as-placeholder',
            },
            orderReferenceErrorLabel: {
                type: String,
                atrribute: 'order-reference-error-label',
            },
        };
    }

    constructor() {
        super();
        this.pageSize = 30; // Default size
        this.isUserLoggedIn = true;
        this.title = '';
        this.hideOptional = false;
        this.placeholder = '';
        this.required = true;
        this.orderFetched = false;
        this.selectedValue = '';
        this.syncSelectValue = true;
        this.disabledNativePlaceholderOption = true;
        this.useLabelAsPlaceholder = true;
        this.errorLabel = '';
        this.validationEnabled = false;
        this.localError = false;
        this.orderReferenceErrorLabel = '';
    }

    firstUpdated() {
        super.firstUpdated();
        this.dispatchEvent(new CustomEvent('preserve-phoneNumber-pattern', { detail: true }));
    }

    updated() {
        if (this.isUserLoggedIn === 'current' && !this.orderFetched) {
            this.orderFetched = true;
            this.dispatchEvent(new CustomEvent('fetch-orders', { detail: { size: this.size } }));
        }

        if (this.isOrder() && !this.validationEnabled) {
            this.validationEnabled = true;
            this.dispatchEvent(
                new CustomEvent('set-validations', {
                    detail: {
                        name: this.fieldName,
                        validations: {
                            required: this.required,
                            constraints: [
                                {
                                    deferred: false,
                                    errorMessage: this.errorLabel,
                                    parameters: {},
                                    type: 'required',
                                    valid: true,
                                },
                                {
                                    deferred: true,
                                    errorMessage: this.orderReferenceErrorLabel,
                                    parameters: { max: 25, min: 0 },
                                    type: 'length',
                                },
                            ],
                            value: null,
                        },
                    },
                }),
            );
        }
    }

    onSelect = evt => {
        this.dispatchEvent(
            new CustomEvent('set-order-value', { detail: { orderNumber: evt.detail } }),
        );
    };

    handleError = evt => {
        if (evt && evt.detail) {
            this.localError = evt.detail;
        }
    };

    isOrder = () => this.subject === personalInformation.ORDER_LABEL;

    getPlaceholder = () => {
        return this.fieldLabel || this.fieldName;
    };

    renderSelectComponent = () => {
        const value = this.selectedValue || this.defaultValue;
        return this.orders.length > 0
            ? html`
                  <div
                      class="contact-us-order-list ${this.error || this.localError
                          ? 'contact-us-order-list--error'
                          : ''} ${this.isOrder() ? '' : 'd-none'}"
                  >
                      <e2-contact-us-select
                          class="native-select native-select--form-field"
                          class-modifier="select--form-field"
                          .options="${this.orders}"
                          ?disabled="${this.readOnly}"
                          ?has-errors="${this.error || this.localError}"
                          @select="${this.onSelect}"
                          @showError=${this.handleError}
                          error-label="${this.errorLabel}"
                          default-value="${value || ''}"
                          ?labelled="${this.fieldLabel}"
                          ?sync-select-value="${!!this.syncSelectValue}"
                          ?disabled-placeholder-option="${this.disabledNativePlaceholderOption}"
                          place-holder="${this.getPlaceholder()}"
                          required="${this.required}"
                      >
                      </e2-contact-us-select>
                  </div>
              `
            : null;
    };

    render() {
        return this.isUserLoggedIn
            ? this.renderSelectComponent()
            : html`
                  <div class="${this.isOrder() ? '' : 'd-none'}">
                      <v-slot name="guestContent"></v-slot>
                  </div>
              `;
    }
}

const mapStateToProps = (state, ctx) => {
    const selectors = cacheSelector(ctx, getSelectors);
    return {
        orders: orderSelectors.getOrders(state),
        error: selectors.getDataFieldError(state, ctx.fieldName),
        isUserLoggedIn: userSelectors.getUserId(state),
        subject: orderSelectors.getSubjectValue(state),
        selectedValue: personalSelectors.getOrderSelected(state) || '',
    };
};

const mapDispatchEvents = (dispatch, ctx) => {
    return {
        'fetch-orders': ({ detail: { size } }) => dispatch(orderActions.loadOrders({ size })),
        'set-validations': ({ detail: { name, validations } }) =>
            dispatch(personalActions.setFakeValidation({ name, validations })),
        'set-order-value': ({ detail: { orderNumber } }) =>
            dispatch(FormAction.setDatafieldValue(ctx.storeName, ctx.fieldName, orderNumber)),
        'preserve-phoneNumber-pattern': ({ detail }) =>
            dispatch(personalActions.preservePhoneNumberPattern(detail)),
    };
};

export default () =>
    customElements.define(
        'e2-contact-us-orders',
        connect(mapStateToProps, mapDispatchEvents)(ContactUsOrderSelection),
    );
